import { customInstance } from '@fukuri-admins/apis/client/base/axiosInstance';
import {
  getGetApiV1ThreeLetterCodePartnerAdminsBankAccountsKey,
  getGetApiV1ThreeLetterCodePartnerAdminsBankAccountsUrl,
} from '@fukuri-admins/apis/client/gen/endpoints';
import type { GetApiV1ThreeLetterCodePartnerAdminsBankAccounts200One } from '@fukuri-admins/apis/client/gen/model';
import { useFetch } from '@fukuri-admins/hooks/common';

type Args = {
  /** パートナーの 3 文字コード */
  threeLetterCode: string;
  /** 口座情報を取得する user_id の配列（指定した user_id のうち口座連携済みのレコードのみ返す） */
  userIds: number[];
};

type IUseGetPartnerAdminsBankAccounts = (args: Args) => {
  /**
   * 口座情報一覧データ。
   * userIds が空配列のときは API を発火しないため undefined になる（依存フェッチ）。
   */
  data: GetApiV1ThreeLetterCodePartnerAdminsBankAccounts200One | undefined;
};

/**
 * パートナー管理者向け口座情報一覧取得フック
 *
 * - userIds に利用者 ID の配列を渡すことで、指定ユーザーの口座連携済みレコードのみを取得する
 * - userIds が空配列のときは SWR キーを null にして発火しない（依存フェッチ）
 * - user_ids[] はキー繰り返し形式（user_ids[]=1&user_ids[]=2）で送信される
 *   （orval.config.ts の injectExplodeTrue transformer が openapi.yml の配列クエリパラメータに
 *    explode:true を注入し、Orval が URLSearchParams.append のキー繰り返し形式を生成するため）
 */
export const useGetPartnerAdminsBankAccounts: IUseGetPartnerAdminsBankAccounts = ({
  threeLetterCode,
  userIds,
}) => {
  const params = userIds.length > 0 ? { 'user_ids[]': userIds } : undefined;

  const { data } = useFetch<GetApiV1ThreeLetterCodePartnerAdminsBankAccounts200One>(
    userIds.length > 0
      ? getGetApiV1ThreeLetterCodePartnerAdminsBankAccountsKey(threeLetterCode, params)
      : null,
    () =>
      customInstance<GetApiV1ThreeLetterCodePartnerAdminsBankAccounts200One>(
        getGetApiV1ThreeLetterCodePartnerAdminsBankAccountsUrl(threeLetterCode, params),
        { method: 'GET' }
      ),
    { suspense: true }
  );

  return {
    data,
  };
};
